import React from 'react';
import StaffProfileTemplate, {
  ProfileSection,
  ProfileParagraphs,
  ProfileBullets,
  ProfilePublications,
  ProfileContactSection,
} from './StaffProfileTemplate';

const EkRajOjhaProfile = () => {
  const person = {
    id: 'ek-raj-ojha',
    name: 'Ek Raj Ojha',
    honorific: 'Dr',
    title: 'Highland Fellow',
    affiliation: 'Rural and Regional Development Planner, Nepal',
    image: 'https://customer-assets-0z36b82j.emergentagent.net/job_7a4f11f8-6c06-4e1c-b433-73d9453c195e/artifacts/12bov734_Picture1.jpg',
    email: 'drekrajojha@gmail.com',
    category: 'Fellows & Visiting Scholars',
    testid: 'ek-raj-ojha-profile',
  };

  return (
    <StaffProfileTemplate person={person}>
      {/* Intellectual Biography */}
      <ProfileSection title="Intellectual Biography">
        <ProfileParagraphs
          items={[
            'Dr. Ek Raj Ojha is an agriculturist by initial training and a rural and regional development planner by specialization. He has over thirty-five years of multidisciplinary experience in higher education and sustainable development across Asia, with a strong focus on Nepal.',
            'His career has encompassed teaching, research, publications, curriculum development, institutional leadership, project design, planning, implementation, monitoring, and evaluation for national and international organizations.',
            'He has held senior positions including Executive Director of SAPPROS Nepal, College Head of GEMS Institute of Higher Education, Associate Professor and Associate Director at Kathmandu University, Development Economist with USAID Nepal, and Researcher with the United Nations Centre for Regional Development in Nagoya, Japan.',
            'Dr. Ojha spent fourteen years (1986–2000) at Nepal Rastra Bank (Central Bank of Nepal), progressing from Agriculture Officer to Research Officer and Senior Instructor at its Bankers\' Training Centre, where he coordinated central banking training and led research on rural credit, self-help groups, and the financial sector.',
          ]}
        />
      </ProfileSection>

      {/* Research Foci & Areas of Work */}
      <ProfileSection title="Research Foci & Areas of Work">
        <ProfileBullets
          items={[
            'Agricultural production and rural development',
            'Sustainable integrated human settlements and land use planning',
            'Environmental economics and natural resource management',
            'Highland ecosystems and mountain agro-ecosystems',
            'Governance, entrepreneurship, and corporate ethics',
            'Gender analysis and production credit for rural women',
            'Rural and regional planning',
            'Community development',
            'Inclusive and sustainable development in Nepal',
          ]}
        />
      </ProfileSection>

      {/* Highland Institute Projects */}
      <ProfileSection title="Highland Institute Projects">
        <ProfileParagraphs
          items={[
            'Dr. Ojha brings decades of expertise in highland ecosystems, sustainable development, and regional planning to The Highland Institute. His extensive research on mountain agro-ecosystems in Nepal and his work on agricultural terracing and ecosystem dynamics offer valuable perspectives for comparative highland studies across Asia.',
            'His background in development planning and institutional leadership supports the Institute\'s mission to build sustainable knowledge infrastructures in frontier regions.',
          ]}
        />
      </ProfileSection>

      {/* Education */}
      <ProfileSection title="Education">
        <ProfileBullets
          items={[
            'PhD in Rural and Regional Development Planning, Human Settlements Development Planning Division, School of Environment, Resources and Development (SERD), Asian Institute of Technology (AIT), Bangkok (1995) — Dissertation: "Ecosystem dynamics in the hills of far-western Nepal"',
            'MSc in Rural and Regional Development Planning, Asian Institute of Technology (AIT), Bangkok (1990) — Thesis: "Production Credit for Rural Women project in Dhading district, Nepal"',
            'BSc Agriculture, University of Agricultural Sciences, Bangalore (1984)',
          ]}
        />
      </ProfileSection>

      {/* Selected Publications & Knowledge Outputs */}
      <ProfileSection title="Selected Publications & Knowledge Outputs">
        <ProfileParagraphs
          items={[
            'Dr. Ojha has authored more than sixty-five publications, including books, monographs, and articles on sustainable development, highland ecosystems, gender inclusion, and local governance.',
          ]}
        />
        <h3 className="font-semibold text-stone-900 mt-6 mb-4 not-prose">Selected Books</h3>
        <ProfilePublications
          items={[
            {
              year: '2017',
              citation: (
                <>
                  <em>Agricultural Terracing: Development Perspectives</em>, Revised Second Edition, American Academic Press, Miami.
                </>
              ),
            },
            {
              year: '2002',
              citation: (
                <>
                  <em>Possessions, Problems and Prospects of Mountains: Special Reference to Nepal and Its Far-western Region</em>, Ekta Books, Kathmandu.
                </>
              ),
            },
            {
              year: '1999',
              citation: (
                <>
                  <em>Dynamics and Development of Highland Ecosystems: Highlights on the Hills in Far-western Nepal</em>, Walden Book House, Kathmandu.
                </>
              ),
            },
            {
              year: '1993',
              citation: (
                <>
                  Ojha, E.R. and K.E. Weber, <em>Production Credit for Rural Women: An Impact Evaluation of the PCRW Project around Gajuri, Nepal</em>, Human Settlements Development Monograph 32, AIT, Bangkok.
                </>
              ),
            },
          ]}
        />
        <h3 className="font-semibold text-stone-900 mt-8 mb-4 not-prose">Selected Articles</h3>
        <ProfilePublications
          items={[
            {
              year: '2022',
              citation: (
                <>
                  'Sustaining Social Forestry for Sustainable Human Development' in <em>Proceedings of the 9th Sustainable Development Conference (SDC2022)</em>, Bangkok.
                </>
              ),
            },
            {
              year: '2018',
              citation: (
                <>
                  'Need for Promoting Agriculture and Protecting the Environment' (editorial) in <em>Advances in Agriculture and Environmental Science: Open Access Journal</em>, Vol. 1, No. 1.
                </>
              ),
            },
            {
              year: '2001',
              citation: (
                <>
                  'Sustaining Mountain Agro-ecosystems Through Sustenance of Agricultural Terrace Systems' in <em>Botanica Orientalis: Journal of Plant Science</em>, Central Department of Botany, Tribhuvan University, Kathmandu.
                </>
              ),
            },
            {
              year: '1997',
              citation: (
                <>
                  'Highland Ecosystems in Nepal: Why and How to Develop Them?' in <em>Space Informatics for Mountain Resources Management</em>, United Nations Centre for Regional Development (UNCRD), Nagoya.
                </>
              ),
            },
            {
              year: '1997',
              citation: (
                <>
                  'Productivity Dynamics of Agricultural Terraces in the Hills of Far Western Nepal' in <em>Green Productivity: In Pursuit of Better Quality of Life</em>, Asian Productivity Organization (APO), Tokyo.
                </>
              ),
            },
          ]}
        />
      </ProfileSection>

      {/* Public Engagement, Teaching & Community Work */}
      <ProfileSection title="Public Engagement, Teaching & Community Work">
        <ProfileParagraphs
          items={[
            'Dr. Ojha is a frequent commentator on Nepal\'s development in national television and print media.',
            'He has served as visiting faculty at numerous Nepali universities and colleges, including Tribhuvan University, Kathmandu University, Purbanchal University, and Sikkim Manipal University (2000–2014). He has been adjunct faculty delivering MBA modules for Glasgow Caledonian University and Queen Margaret University at Ace International Business School, Kathmandu (2020–2023), and has served as Academic Counsellor for the MBA and BBA programmes of the Indira Gandhi National Open University (IGNOU) since 2023.',
            'He has supervised more than one hundred postgraduate research works in rural development and environmental planning and has served as an external examiner for numerous theses. He has also served as Editor-in-Chief, Executive Editor, or technical editor for several institutional publications and journals.',
          ]}
        />
      </ProfileSection>

      {/* Contact & Scholarly Infrastructure */}
      <ProfileContactSection
        email={person.email}
        role="Highland Fellow"
        affiliationLines={[
          'Nepal',
          'Tel: +977 9841221788'
        ]}
        testidPrefix="ekraj"
        scholarlyProfiles={[
          { label: 'LinkedIn', url: 'https://www.linkedin.com/in/ek-raj-ojha/', testid: 'ekraj-linkedin-link' },
        ]}
      />
    </StaffProfileTemplate>
  );
};

export default EkRajOjhaProfile;
